
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

@Component
export default class Preloader extends Vue {
  @Prop({ type: Boolean, default: false }) readonly disableIntroBlur!: boolean;
  @Prop({ type: Boolean, default: false }) readonly startImmediately!: boolean;
  isLoading = true;
  public isFadingOut = false;
  private readonly fadeDurationMs = 700;
  private hasStartedLoadingLifecycle = false;

  mounted() {
    if (this.startImmediately || this.$route.path !== '/') {
      this.startLoadingLifecycle();
    }

    this.$watch(() => this.startImmediately, (shouldStart: boolean) => {
      if (shouldStart) {
        this.startLoadingLifecycle();
      }
    });

    this.$watch(() => this.$route.path, (path: string) => {
      if (path !== '/') {
        this.startLoadingLifecycle();
      }
    });
  }

  private startLoadingLifecycle() {
    if (this.hasStartedLoadingLifecycle) {
      return;
    }
    this.hasStartedLoadingLifecycle = true;
    // On app routes, wait for authentication and basic loading.
    this.waitForBasicReady();
  }

  private async waitForBasicReady() {
    try {
      // Add a fallback timeout for non-homepage routes (shorter than homepage)
      const timeoutPromise = new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, 8000); // 8 second timeout for non-homepage routes
      });

      // Main loading promise
      const loadingPromise = this.performBasicLoading();

      // Race between loading and timeout
      await Promise.race([loadingPromise, timeoutPromise]);
      
      // Start fade out animation
      this.$emit('fade-start');
      this.isFadingOut = true;
      
      // Wait for fade out animation to complete
      await new Promise(resolve => setTimeout(resolve, this.fadeDurationMs));
      
      this.isLoading = false;
      this.$emit('complete');
    } catch (error) {
      console.error('Error during basic app loading:', error);
      await this.finishLoading();
    }
  }

  private async finishLoading() {
    if (!this.isFadingOut) {
      this.$emit('fade-start');
      this.isFadingOut = true;
      await new Promise(resolve => setTimeout(resolve, this.fadeDurationMs));
    }
    this.isLoading = false;
    this.$emit('complete');
  }

  private async performBasicLoading() {
    // Wait for Vue to be fully mounted
    await this.$nextTick();

    // Run independent checks in parallel to reduce total blocking time.
    await Promise.all([
      this.preloadEssentialUIImages(),
      this.waitForRouter(),
      this.waitForStore(),
      this.waitForServices(),
      this.waitForAuthentication(),
      this.waitForBasicPageContent(),
    ]);

    // Small delay to ensure smooth transition
    await new Promise(resolve => setTimeout(resolve, 60));
  }

  private async preloadEssentialUIImages(): Promise<void> {
    // Keep only truly critical modal icons blocking.
    const blockingEssentialImages = [
      '/img/IconRawsWatermark.png',
      '/img/IconRawsDelete.png',
      '/img/anti-ai/grid.png',
    ];

    const backgroundEssentialImages = [
      '/img/anti-ai/noise.png',
      '/img/anti-ai/lines.png',
      '/img/IconRawsHelp.png',
      '/img/IconRawsMailman.png',
      '/img/IconRawsFolders.png',
      require('@/assets/backend/watermark_placeholder.jpg'),
      require('@/assets/waitingbg.gif'),
      require('@/assets/dropzone.png'),
    ];

    await this.preloadImagesBlocking(blockingEssentialImages, 1200);
    this.preloadImagesInBackground(backgroundEssentialImages);
  }

  private preloadImage(src: string, timeoutMs = 1200): Promise<void> {
    return new Promise<void>((resolve) => {
      const img = new Image();
      let resolved = false;

      const done = () => {
        if (!resolved) {
          resolved = true;
          resolve();
        }
      };

      const timeout = setTimeout(done, timeoutMs);
      img.onload = () => {
        clearTimeout(timeout);
        done();
      };
      img.onerror = () => {
        clearTimeout(timeout);
        done();
      };
      img.src = src;
    });
  }

  private async preloadImagesBlocking(images: string[], timeoutMs = 1200): Promise<void> {
    await Promise.all(images.map((src) => this.preloadImage(src, timeoutMs)));
  }

  private preloadImagesInBackground(images: string[]): void {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }

  private async waitForRouter(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.$router && this.$router.currentRoute) {
        resolve();
      } else {
        // Wait for router to be ready
        const checkRouter = () => {
          if (this.$router && this.$router.currentRoute) {
            resolve();
          } else {
            setTimeout(checkRouter, 50);
          }
        };
        checkRouter();
      }
    });
  }

  private async waitForStore(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.$store) {
        resolve();
      } else {
        // Wait for store to be ready
        const checkStore = () => {
          if (this.$store) {
            resolve();
          } else {
            setTimeout(checkStore, 50);
          }
        };
        checkStore();
      }
    });
  }

  private async waitForServices(): Promise<void> {
    return new Promise<void>((resolve) => {
      // Wait for critical services to be initialized
      // This includes waiting for any async service initialization
      const checkServices = () => {
        // Check if critical services are available
        if (typeof window.fetch === 'function' && window.localStorage) {
          resolve();
        } else {
          setTimeout(checkServices, 50);
        }
      };
      checkServices();
    });
  }

  private async waitForAuthentication(): Promise<void> {
    return new Promise<void>((resolve) => {
      const maxWaitMs = 1200;
      const startTime = Date.now();

      // Wait for authentication state to be resolved
      const checkAuth = () => {
        // Access the user store to check login state
        const userModule = this.$store.state.user;
        if (userModule && userModule.loginState !== 'unknown') {
          // Allow one short tick for route/UI to settle.
          setTimeout(resolve, 50);
        } else if (Date.now() - startTime >= maxWaitMs) {
          // Do not block the preloader forever when auth resolution is delayed.
          resolve();
        } else {
          setTimeout(checkAuth, 50);
        }
      };
      checkAuth();
    });
  }

  private async waitForBasicPageContent(): Promise<void> {
    return new Promise<void>((resolve) => {
      const maxWaitMs = 1400;
      const startTime = Date.now();

      // Wait for basic page structure to be rendered and route content to be loaded
      const checkPageContent = () => {
        if (Date.now() - startTime >= maxWaitMs) {
          resolve();
          return;
        }

        // Check if the main content container is present
        const mainContent = document.querySelector('.v-main');
        const contentArea = document.querySelector('.content');
        
        // Also check for specific route content
        const sessionListContent = document.querySelector('.v-container'); // SessionList
        const sessionContent = document.querySelector('.session-container'); // Session view
        const homeContent = document.querySelector('.section_1'); // Home
        
        // Check if we have main structure AND some actual content
        if (mainContent && contentArea && (sessionListContent || sessionContent || homeContent || document.querySelector('.v-flex'))) {
          // Short settle time after content appears.
          setTimeout(resolve, 80);
        } else {
          setTimeout(checkPageContent, 80);
        }
      };
      
      // Start checking after a short delay to allow Vue to render
      setTimeout(checkPageContent, 80);
    });
  }

}
